import React, { useCallback, useMemo, useState } from "react";

import AgentCard from "./AgentCard";
import { isAgentLive, useMesaAgent } from "./useMesaAgent";

/**
 * The agent showcase.
 *
 * The gallery owns the call rather than each card, because the Mesa widget is a
 * page-level singleton: `window.DograhWidget` is defined once and a second
 * script tag silently keeps the first one's manager. One agent holds the
 * microphone at a time, and opening a second panel closes the first.
 */

const AgentGallery = ({ agents }) => {
  const [activeId, setActiveId] = useState(null);

  const activeAgent = useMemo(
    () => agents.find((a) => a.id === activeId) || null,
    [agents, activeId],
  );

  const call = useMesaAgent(activeAgent);
  const { start } = call;

  /**
   * Select and dial in the same handler.
   *
   * Both halves have to happen inside the click. `setActiveId` alone would
   * leave the dial to an effect on the next tick, which is a separate task and
   * has lost the user activation Safari needs to play the remote audio. So the
   * agent is passed to `start` explicitly instead of waiting for the hook to
   * rebind.
   */
  const open = useCallback(
    (id) => {
      const next = agents.find((a) => a.id === id);
      if (!next) return;
      setActiveId(id);
      start(next);
    },
    [agents, start],
  );

  const close = useCallback(() => setActiveId(null), []);

  const anyLive = agents.some(isAgentLive);

  return (
    <section className="agent-gallery">
      {/* The heading only promises a call when one can actually be placed.
          "Ring one of them" above four cards with no buttons reads as a bug. */}
      <div className="agent-gallery-head">
        <h2>{anyLive ? "Ring one of them" : "The agents"}</h2>
        <p className="agent-gallery-sub">
          {anyLive
            ? "These are the production lines, not a recording. Pick an agent, allow the microphone, and talk to it."
            : "Four agents on one engine, each answering a real phone line. Same graph, same tool runtime, four different definitions of a bad call."}
        </p>
      </div>

      <div className="agent-grid">
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            live={isAgentLive(agent)}
            active={activeId === agent.id}
            call={call}
            onOpen={open}
            onClose={close}
          />
        ))}
      </div>
    </section>
  );
};

export default AgentGallery;
